import {
  ArrowLeftOutlined,
  AudioOutlined,
  CalendarOutlined,
  EditOutlined,
  EyeOutlined,
  FormOutlined,
  SaveOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography
} from 'antd';
import { useGetOneAdminAccessQuery } from 'api/AdminAccessApi';
import {
  useGetOneAdminAccessFileMessageQuery,
  useGetOneAdminAccessMessageQuery,
  usePatchAdminAccessMessageMutation
} from 'api/AdminAccessMessagesApi';
import { MessageEventTypeEnum, MessageStatusEnum, MessageTypeEnum } from 'api/MessagesApi';
import AddRecipientModal, {
  ADD_RECIPIENT_MODAL_NAME
} from 'components/Admin/BeneficiaryMessageComponent/AddRecipientModal';
import UpdateContactsModal, {
  UPDATE_CONTACTS_MODAL_NAME
} from 'components/Admin/BeneficiaryMessageComponent/UpdateContactsModal';
import DownloadLink from 'components/Message/DownloadLink/DownloadLink';
import MessageIcon from 'components/Message/MessageIcon';
import { AppAudioPlayer } from 'components/UiKit/Media/AppAudioPlayer';
import { AppVideoPlayer } from 'components/UiKit/Media/AppVideoPlayer';
import { TextToHtmlComponent } from 'components/UiKit/TextToHtmlComponent/TextToHtmlComponent';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { openModal } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import dayjs from 'utils/dayjs';
import {
  generateRange,
  getAdminAccessDisplayName,
  getFullNameOrEmail,
  getMonthNames,
  isAudioFile,
  isVideoFile,
  logging
} from 'utils/utils';
import './BeneficiaryMessageComponent.css';

function BeneficiaryMessageComponent() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { adminAccessId, messageId } = useParams<{ adminAccessId: string; messageId: string }>();
  const [form] = Form.useForm();
  const [deliveryForm] = Form.useForm();
  const [showContent, setShowContent] = useState(false);
  const [eventType, setEventType] = useState<MessageEventTypeEnum>(MessageEventTypeEnum.Final);

  const { data: adminAccess, isFetching: isFetchingAdmin } = useGetOneAdminAccessQuery(
    { id: adminAccessId! },
    { skip: !adminAccessId }
  );

  const {
    data: message,
    isFetching: isFetchingMessage,
    isError,
    error
  } = useGetOneAdminAccessMessageQuery(
    { id: adminAccessId!, messageId: messageId! },
    { skip: !adminAccessId || !messageId }
  );

  const { data: messageFile, isFetching: isFetchingFile } = useGetOneAdminAccessFileMessageQuery(
    { id: adminAccessId!, messageId: messageId! },
    { skip: !adminAccessId || !messageId }
  );

  const [patchMessage, { isLoading: isPatching }] = usePatchAdminAccessMessageMutation();

  useEffect(() => {
    if (message) {
      form.setFieldsValue({
        contacts: message.contacts.map((contact) => ({
          id: contact.id,
          name: contact.name,
          email: contact.email,
          phone: contact.phone || ''
        }))
      });

      const messageEventType = (message.eventType || MessageEventTypeEnum.Final) as MessageEventTypeEnum;
      setEventType(messageEventType);
      deliveryForm.setFieldsValue({
        eventType: messageEventType,
        deliveryYear: message.deliveryYear,
        deliveryMonth: message.deliveryMonth,
        deliveryDay: message.deliveryDay
      });
    }
  }, [message, form, deliveryForm]);

  const renderStatus = (status?: MessageStatusEnum) => {
    if (!status) return null;
    const statuses = {
      [MessageStatusEnum.Published]: <Tag color="warning">Scheduled</Tag>,
      [MessageStatusEnum.Delivering]: <Tag color="geekblue">Delivering</Tag>,
      [MessageStatusEnum.Delivered]: <Tag color="geekblue">Delivered</Tag>
    };

    return statuses[status] ? statuses[status] : <Tag color="default">Draft</Tag>;
  };

  const renderTypeIcon = (type?: MessageTypeEnum) => {
    if (type === MessageTypeEnum.Video) {
      return (
        <div className="message-type-icon video">
          <VideoCameraOutlined style={{ fontSize: 24, color: 'var(--primary-color)' }} />
        </div>
      );
    } else if (type === MessageTypeEnum.Audio) {
      return (
        <div className="message-type-icon audio">
          <AudioOutlined style={{ fontSize: 24, color: 'var(--primary-color)' }} />
        </div>
      );
    } else {
      return (
        <div className="message-type-icon text">
          <FormOutlined style={{ fontSize: 24, color: 'var(--primary-color)' }} />
        </div>
      );
    }
  };

  const handleSaveContact = async (index: number) => {
    try {
      const values = await form.validateFields();
      const newContact = values.contacts[index];
      const oldContact = message?.contacts[index];

      if (!adminAccessId || !messageId || !oldContact) return;

      dispatch(
        openModal({
          name: UPDATE_CONTACTS_MODAL_NAME,
          data: {
            adminAccessId,
            messageId,
            message,
            senderName: getFullNameOrEmail(message.user),
            oldContact: {
              id: oldContact.id,
              name: oldContact.name,
              email: oldContact.email,
              phone: oldContact.phone
            },
            newContact,
            allContacts: message.contacts,
            contactIndex: index
          }
        })
      );
    } catch (e) {
      logging(e);
    }
  };

  const handleAddContact = async () => {
    dispatch(
      openModal({
        name: ADD_RECIPIENT_MODAL_NAME,
        data: {
          adminAccessId,
          messageId,
          currentContacts: form.getFieldValue('contacts') || []
        }
      })
    );
  };

  const handleRemoveContact = async (index: number, remove: (index: number) => void) => {
    if (!adminAccessId || !messageId) return;

    remove(index);

    try {
      await patchMessage({
        id: adminAccessId,
        messageId: messageId,
        contacts: form.getFieldValue('contacts')
      }).unwrap();
    } catch (e) {
      logging(e);
    }
  };

  const handleSaveDeliverySettings = async () => {
    try {
      const values = await deliveryForm.validateFields();
      if (!adminAccessId || !messageId) return;

      await patchMessage({
        id: adminAccessId,
        messageId: messageId,
        ...values
      }).unwrap();
    } catch (e) {
      logging(e);
    }
  };

  if (isFetchingAdmin || isFetchingMessage) {
    return <Skeleton active />;
  }

  if (isError) {
    return <Alert message={(error as any)?.data?.message || 'Error loading message'} type="error" showIcon />;
  }

  return (
    <div className="beneficiary-message-component">
      <Button className="back-btn" type="text" onClick={() => navigate(`/beneficiaries/${adminAccessId}`)}>
        <ArrowLeftOutlined />
        Back to {getAdminAccessDisplayName(adminAccess || {}, '—')}'s
        Messages
      </Button>

      <Flex align="center" gap={16} className="message-header">
        {renderTypeIcon(message?.type)}
        <div className="message-info" style={{ flex: 1 }}>
          <Flex align="center" justify="space-between" gap={8}>
            <div className="message-title">
              {message?.type === MessageTypeEnum.Text
                ? 'Written Message'
                : message?.type === MessageTypeEnum.Video
                  ? 'Video Message'
                  : 'Audio Message'}
            </div>
            {renderStatus(message?.status)}
          </Flex>
          <div className="message-meta">
            Created {dayjs(message?.createdAt).format('MMM D, YYYY')} · For{' '}
            {message?.contacts.map((c) => c.name).join(', ')}
          </div>
        </div>
      </Flex>

      <Flex vertical={true} gap={24}>
        <Card className="message-content-card">
          <Flex justify="space-between" align="center" gap={8}>
            <div className="message-content-title">Message Content</div>
            <Button icon={<EyeOutlined />} onClick={() => setShowContent((v) => !v)}>
              {showContent ? 'Hide Content' : 'View Content'}
            </Button>
          </Flex>

          {showContent && (
            <div className="message-content-box" style={{ marginTop: 16 }}>
              {isFetchingMessage || isFetchingFile ? (
                <Skeleton active />
              ) : message ? (
                <>
                  <div className="message-content-box-inner">
                    {message.fileName ? (
                      (() => {
                        const fallback = (
                          <Space size="small">
                            <MessageIcon type={message.type} />
                            <DownloadLink
                              message={message as any}
                              messageFile={messageFile as any}
                              text={message.fileName}
                            />
                          </Space>
                        );

                        if (message.type === MessageTypeEnum.Video && isVideoFile(message.fileName)) {
                          return (
                            <AppVideoPlayer
                              url={messageFile?.signedUrl}
                              fileName={message.fileName}
                              fallback={fallback}
                            />
                          );
                        }

                        if (message.type === MessageTypeEnum.Audio && isAudioFile(message.fileName)) {
                          return (
                            <AppAudioPlayer
                              url={messageFile?.signedUrl}
                              fileName={message.fileName}
                              fallback={fallback}
                            />
                          );
                        }

                        return fallback;
                      })()
                    ) : message.type === MessageTypeEnum.Text ? (
                      <TextToHtmlComponent text={message.content} />
                    ) : null}
                  </div>

                  {message && (
                    <div>
                      Click <DownloadLink message={message as any} messageFile={messageFile as any} text="here" /> to
                      download
                    </div>
                  )}
                  <div>As an administrator, you can view but not edit message content.</div>
                </>
              ) : null}
            </div>
          )}
        </Card>

        <Card
          className="recipient-info-card"
          title={
            <Flex align="center" gap={12}>
              <CalendarOutlined style={{ fontSize: 20, color: '#4c4e64' }} />
              <div className="message-content-title">Recipient Contact Information</div>
            </Flex>
          }
          extra={
            <Button type="text" icon={<EditOutlined />} onClick={handleAddContact}>
              Add Recipient
            </Button>
          }
        >
          <Form form={form} layout="vertical">
            <Form.List name="contacts">
              {(fields, { remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => {
                    const contact = form.getFieldValue(['contacts', name]);
                    return (
                      <div key={key} className="recipient-form-item">
                        <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
                          <Typography.Text strong>
                            Recipient {index + 1} {contact?.name ? `— ${contact.name}` : ''}
                          </Typography.Text>
                          <Popconfirm
                            title="Delete contact"
                            description="Are you sure you want to delete this contact?"
                            onConfirm={() => handleRemoveContact(index, remove)}
                            okText="Yes"
                            cancelText="No"
                            okButtonProps={{ loading: isPatching }}
                          >
                            <Button type="text" danger loading={isPatching}>
                              Remove
                            </Button>
                          </Popconfirm>
                        </Flex>

                        <Form.Item {...restField} name={[name, 'id']} noStyle>
                          <Input type="hidden" />
                        </Form.Item>

                        <Form.Item {...restField} label="Name" name={[name, 'name']} rules={[{ required: true }]}>
                          <Input disabled={true} />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="Email Address"
                          name={[name, 'email']}
                          rules={[{ type: 'email', message: 'Invalid email' }, { required: true }]}
                        >
                          <Input />
                        </Form.Item>

                        <Form.Item {...restField} label="Phone Number" name={[name, 'phone']}>
                          <Input />
                        </Form.Item>

                        <Button type="primary" onClick={() => handleSaveContact(index)} loading={isPatching}>
                          Save Contact Changes
                        </Button>
                      </div>
                    );
                  })}
                </>
              )}
            </Form.List>
          </Form>
        </Card>

        {eventType !== MessageEventTypeEnum.Final && (
          <Card className="delivery-settings-card">
            <Flex align="center" gap={12} style={{ marginBottom: 24 }}>
              <CalendarOutlined style={{ fontSize: 20, color: '#4c4e64' }} />
              <div className="message-content-title">Delivery Settings</div>
            </Flex>

            <Form
              form={deliveryForm}
              layout="vertical"
              onValuesChange={(changedValues) => {
                if (changedValues.eventType) {
                  setEventType(changedValues.eventType);
                }
              }}
            >
              {message?.eventHint && <p>Future event: {message.eventHint}</p>}
              <Form.Item label="Delivery Event" name="eventType">
                <Select
                  options={[
                    { label: 'Birthday', value: MessageEventTypeEnum.Birthday },
                    { label: 'Holiday', value: MessageEventTypeEnum.Holiday },
                    { label: 'Anniversary', value: MessageEventTypeEnum.Anniversary },
                    { label: 'Other/Custom', value: MessageEventTypeEnum.Other },
                    { label: 'Future Event', value: MessageEventTypeEnum.Future }
                  ]}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Year" name="deliveryYear" rules={[{ required: true }]}>
                    <Select
                      placeholder="Year"
                      allowClear
                      options={generateRange(new Date().getFullYear(), new Date().getFullYear() + 50).map((year) => ({
                        label: year,
                        value: year
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Month" name="deliveryMonth" rules={[{ required: true }]}>
                    <Select
                      placeholder="Month"
                      options={getMonthNames().map((month, index) => ({
                        label: month,
                        value: index + 1
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Day" name="deliveryDay" rules={[{ required: true }]}>
                    <Select
                      placeholder="Day"
                      options={generateRange(1, 31).map((day) => ({
                        label: day,
                        value: day
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveDeliverySettings} loading={isPatching}>
                Save Delivery Settings
              </Button>
            </Form>
          </Card>
        )}
      </Flex>

      <AddRecipientModal />
      <UpdateContactsModal />
    </div>
  );
}

export default BeneficiaryMessageComponent;
